import { useMemo } from "react"
import {
  useGraphContext,
  yAxisWidth,
  xAxisHeight,
} from "../GraphContext"
import { graphRangeToUnitsPerLabel } from "./graphRangeToUnitsPerLabel"
import styles from "./YAxis.module.scss"
import { YAxisLabel } from "./YAxisLabel"
import { YAxisSeparator } from "./YAxisSeparator"
import {
  useDisplayGlucoseUnit,
  useGetGlucoseValue,
  useSetGlucoseValue,
} from "src/hooks"

/*
 * the value and units labels that show the user how glucose values on the Y Axis are measured
 */
export function YAxis() {
  const {
    topMgdl,
    bottomMgdl,
  } = useGraphContext()

  const getGlucoseValue = useGetGlucoseValue()
  const setGlucoseValue = useSetGlucoseValue()
  const displayGlucoseUnit = useDisplayGlucoseUnit()

  // how many mgdl between each y axis label
  const mgdlPerLabel = useMemo(
    () => {
      // convert the range to the user's glucose units
      const range = getGlucoseValue(topMgdl - bottomMgdl)
      const interval = graphRangeToUnitsPerLabel(range)
      // convert from user's glucose units back to mgdl
      return setGlucoseValue(interval)
    },
    [
      topMgdl,
      bottomMgdl,
      getGlucoseValue,
      setGlucoseValue,
    ],
  )

  // where to label the yAxis (measured in mgdl)
  const yAxisLabels = useMemo(
    () => {
      const labels: number[] = [
        bottomMgdl,
        topMgdl,
      ]

      const lowestLabel = Math.ceil(bottomMgdl / mgdlPerLabel) * mgdlPerLabel

      for (let value = lowestLabel; value <= topMgdl; value += mgdlPerLabel) {
        // if value is too close to the bottom label, then skip it
        if (Math.abs(value - bottomMgdl) < mgdlPerLabel / 4) {
          continue
        }

        // if value is too close to the top label, then skip it
        if (Math.abs(value - topMgdl) < mgdlPerLabel / 4) {
          continue
        }

        labels.push(value)
      }

      return labels
    },
    [
      bottomMgdl,
      topMgdl,
      mgdlPerLabel,
    ],
  )

  const style = useMemo(
    () => ({
      width: yAxisWidth,
      marginBottom: xAxisHeight,
    }),
    [],
  )

  return (
    <div
      className={styles.container}
      style={style}
    >
      <YAxisSeparator />
      <div className={styles.labels} >
        {yAxisLabels.map((value) => (
          <YAxisLabel
            key={`graph-y-label-${value}`}
            value={value}
          />
        ))}
      </div>
      <div className={styles.unitsContainer}>
        <div
          className={styles.units}
          id="glucose-graph-units"
        >
          {displayGlucoseUnit()}
        </div>
      </div>
    </div>
  )
}
